import { useState, useEffect } from "react";
import { Search, Filter, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "../components/ui/sheet";
import PropertyCard from "../components/PropertyCard";
import ZooplaBranchButton from "../components/ZooplaBranchButton";
import { PropertiesSEO } from "../components/SEO";
import api from "../lib/api";
import logger from "../lib/logger";

const PropertiesPage = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    location: "",
    min_price: "",
    max_price: "",
    bedrooms: "",
    property_type: "",
    available_only: true,
  });

  const locations = [
    "Batley",
    "Beeston", 
    "Bradford",
    "Dewsbury",
    "Harehills",
    "Heckmondwike",
    "Huddersfield",
    "Hyde Park",
    "Leeds",
    "Mirfield",
    "Morley",
    "Thornhill Lees",
    "Tingley",
    "Wakefield"
  ];
  const propertyTypes = ["House", "Apartment", "Flat", "Studio"];
  const bedroomOptions = [1, 2, 3, 4, 5];

  useEffect(() => {
    const fetchProperties = async () => {
      setLoading(true);
      try {
        const data = await api.getProperties(filters);
        setProperties(data);
      } catch (error) {
        logger.error("Error fetching properties:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProperties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (key, value) => {
    // Convert "all" or "any" back to empty string for API
    const actualValue = (value === "all" || value === "any") ? "" : value;
    setFilters((prev) => ({ ...prev, [key]: actualValue }));
  };

  const applyFilters = async () => {
    setLoading(true);
    try {
      const data = await api.getProperties(filters);
      setProperties(data);
    } catch (error) {
      logger.error("Error fetching properties:", error);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = async () => {
    const clearedFilters = {
      location: "",
      min_price: "",
      max_price: "",
      bedrooms: "",
      property_type: "",
      available_only: true,
    };
    setFilters(clearedFilters);
    setLoading(true);
    try {
      const data = await api.getProperties(clearedFilters);
      setProperties(data);
    } catch (error) {
      logger.error("Error fetching properties:", error);
    } finally {
      setLoading(false);
    }
  };

  const hasActiveFilters = filters.location || filters.min_price || filters.max_price || filters.bedrooms || filters.property_type;

  const FilterForm = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-navy mb-2">Location</label>
        <Select value={filters.location || "all"} onValueChange={(v) => handleFilterChange("location", v)}>
          <SelectTrigger data-testid="filter-location">
            <SelectValue placeholder="All locations" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All locations</SelectItem>
            {locations.map((loc) => (
              <SelectItem key={loc} value={loc}>{loc}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-navy mb-2">Min Price</label>
          <Input
            type="number"
            placeholder="£0"
            value={filters.min_price}
            onChange={(e) => handleFilterChange("min_price", e.target.value)}
            data-testid="filter-min-price"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-navy mb-2">Max Price</label>
          <Input
            type="number"
            placeholder="£5000"
            value={filters.max_price}
            onChange={(e) => handleFilterChange("max_price", e.target.value)}
            data-testid="filter-max-price"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-navy mb-2">Bedrooms</label>
        <Select value={filters.bedrooms?.toString() || "any"} onValueChange={(v) => handleFilterChange("bedrooms", v)}>
          <SelectTrigger data-testid="filter-bedrooms">
            <SelectValue placeholder="Any" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any</SelectItem>
            {bedroomOptions.map((num) => (
              <SelectItem key={num} value={num.toString()}>{num}+ beds</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="block text-sm font-medium text-navy mb-2">Property Type</label>
        <Select value={filters.property_type || "all"} onValueChange={(v) => handleFilterChange("property_type", v)}>
          <SelectTrigger data-testid="filter-property-type">
            <SelectValue placeholder="All types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {propertyTypes.map((type) => (
              <SelectItem key={type} value={type}>{type}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex gap-3">
        <Button onClick={applyFilters} className="flex-1 bg-navy hover:bg-navy-light" data-testid="apply-filters-btn">
          Apply Filters
        </Button>
        {hasActiveFilters && (
          <Button onClick={clearFilters} variant="outline" className="border-slate-300" data-testid="clear-filters-btn">
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <div className="py-12 md:py-20" data-testid="properties-page">
      <PropertiesSEO />
      {/* Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
        <h1 className="font-heading text-4xl md:text-5xl font-semibold text-navy mb-4">
          Available Properties
        </h1>
        <p className="text-slate-600 text-lg max-w-2xl mb-6">
          Browse our latest properties to rent across West Yorkshire. Use the filters to find the right home for you.
        </p>
        <div
          className="flex flex-col sm:flex-row items-start sm:items-center gap-4 p-5 rounded-xl border border-[#4C2C79]/15 bg-gradient-to-r from-[#4C2C79]/[0.04] to-transparent"
          data-testid="zoopla-properties-banner"
        >
          <div className="flex-1">
            <p className="font-heading text-navy font-semibold text-lg leading-tight">
              See our full portfolio on Zoopla
            </p>
            <p className="text-slate-600 text-sm mt-1">
              Every Keystone listing, syndicated live to the UK&apos;s leading property portal.
            </p>
          </div>
          <ZooplaBranchButton variant="banner" testId="zoopla-properties-top" />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex gap-8">
          {/* Desktop Filters */}
          <aside className="hidden lg:block w-72 flex-shrink-0">
            <div className="sticky top-28 bg-white rounded-lg border border-slate-200 p-6">
              <h2 className="font-heading font-semibold text-navy mb-6 flex items-center gap-2">
                <Filter className="w-5 h-5" />
                Filter Properties
              </h2>
              <FilterForm />
            </div>
          </aside>

          {/* Main Content */}
          <div className="flex-1">
            {/* Mobile Filter Button */}
            <div className="lg:hidden mb-6">
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="outline" className="w-full border-slate-300" data-testid="mobile-filter-btn">
                    <Filter className="w-4 h-4 mr-2" />
                    Filter Properties
                    {hasActiveFilters && (
                      <span className="ml-2 bg-gold text-navy text-xs px-2 py-0.5 rounded-full">
                        Active
                      </span>
                    )}
                  </Button>
                </SheetTrigger>
                <SheetContent side="left">
                  <SheetHeader>
                    <SheetTitle>Filter Properties</SheetTitle>
                  </SheetHeader>
                  <div className="mt-6">
                    <FilterForm />
                  </div>
                </SheetContent>
              </Sheet>
            </div>

            {/* Results Count */}
            <div className="flex justify-between items-center mb-6">
              <p className="text-slate-600">
                {loading ? "Loading..." : `${properties.length} properties found`}
              </p>
            </div>

            {/* Properties Grid */}
            {loading ? (
              <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="h-96 skeleton rounded-lg" />
                ))}
              </div>
            ) : properties.length > 0 ? (
              <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
                {properties.map((property) => (
                  <PropertyCard key={property.id} property={property} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16 bg-slate-50 rounded-lg">
                <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="font-heading text-xl font-semibold text-navy mb-2">No properties found</h3>
                <p className="text-slate-500 mb-6">Try adjusting your filters, or view our complete live inventory on Zoopla.</p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <Button onClick={clearFilters} variant="outline" className="border-navy text-navy">
                    Clear Filters
                  </Button>
                  <ZooplaBranchButton
                    variant="solid"
                    label="Browse on Zoopla"
                    testId="zoopla-empty-state"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertiesPage;
